import CancelIcon from '@mui/icons-material/Cancel'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Avatar, Box, Button, Grid, IconButton, Modal, Paper, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { translateText } from '../../../language'
import { useThemeMode } from '../../../theme'
import { isDefined, notDefined } from '../../../utils'
import { CardWithAvatar, NoData } from '../widgets'

const getThemeSx = (palette) => ({
  '&': {
    padding: '5px'
  },
  '& .CompanionCard-root': {
    width: '100%',
    marginBottom: '4px',
    '& .CompanionCard-avatar-outer': {
      flex: '0 0 80px',
      maxWidth: '80px',
      padding: '8px',
      '& .MuiAvatar-root': {
        width: '56px',
        height: '56px'
      }
    },
    '& .CompanionCard-avatar-outer + .MuiGrid-item': {
      flex: '1 1 0',
      maxWidth: 'calc(100% - 80px)'
    },
    '& .CompanionCard-title': {
      paddingLeft: '10px'
    },
    '& .CompanionCard-content p': {
      fontStyle: 'italic'
    },
    '& .CompanionCard-content .MuiCardContent-root': {
      paddingTop: '5px',
      paddingBottom: '5px',
      paddingLeft: '10px',
      paddingRight: '5px'
    }
  },
  '& .companion-achievement-card': {
    width: '100%',
    marginBottom: '5px',
    cursor: 'pointer'
  },
  '& .companion-achievement-info-button': {
    color: palette.headerColor
  },
  '& .companion-achievement-info-button svg': {
    color: 'inherit'
  }
})

const modalSx = (palette) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .companion-achievement-modal-content': {
    minWidth: '60%',
    maxWidth: '80%',
    position: 'relative',
    '& .modal-content-title': {
      background: palette.headerColor,
      padding: '10px',
      fontWeight: 600,
      color: 'white'
    },
    '& .companion-achievement-modal-close-button': {
      position: 'absolute',
      width: '20px',
      height: '20px',
      right: '10px',
      top: '10px',
      color: palette.paperColor,
      background: palette.headerColor,
      '&:hover': {
        background: palette.headerBrightColor
      }
    },
    '& .companion-achievement-modal-image': {
      display: 'flex',
      justifyContent: 'center',
      padding: '16px 16px 8px'
    },
    '& .companion-achievement-modal-body': {
      padding: '10px',
      textAlign: 'left',
      maxHeight: '300px',
      overflowY: 'scroll',
      scrollbarWidth: 'thin',
      '&::-webkit-scrollbar': { width: '6px' },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: palette.headerColor,
        borderRadius: '3px',
        opacity: 0.6
      },
      '& p': { margin: '0 0 8px', '&:last-child': { marginBottom: 0 } },
      '& ul, & ol': { paddingLeft: '20px', margin: '4px 0 8px' },
      '& li': { marginBottom: '4px' },
      '& strong': { fontWeight: 600 },
      '& em': { fontStyle: 'italic' }
    }
  }
})

const AchievementCard = (props) => {
  const {
    title,
    description,
    instructions,
    imageSrc,
    awardedLabel
  } = props

  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  const { palette } = useThemeMode()

  const cardText = (
    <Box>
      <ReactMarkdown>{description}</ReactMarkdown>
      {awardedLabel}
    </Box>
  )

  return (
    <Box className='companion-achievement-card' onClick={() => setOpen(true)}>
      <Modal open={open} onClose={() => setOpen(false)} sx={modalSx(palette)} onClick={(e) => e.stopPropagation()}>
        <Box className='companion-achievement-modal-content'>
          <IconButton
            aria-label='close'
            className='companion-achievement-modal-close-button'
            onClick={() => setOpen(false)}
          >
            <CancelIcon />
          </IconButton>
          <Paper>
            <Box className='modal-content-title'>
              <Typography>{title}</Typography>
            </Box>
            <Box className='companion-achievement-modal-image'>
              <Avatar
                src={imageSrc}
                alt={title}
                sx={{ width: 80, height: 80 }}
              >
                {title?.substring(0, 1)}
              </Avatar>
            </Box>
            <Box className='companion-achievement-modal-body'>
              <Typography variant='subtitle1' sx={{ fontWeight: 700, color: palette.headerColor, display: 'block', mb: 0.5 }}>
                {t('achievement-label-description')}
              </Typography>
              <ReactMarkdown>{description}</ReactMarkdown>
              {instructions?.length > 0 && (
                <>
                  <Typography variant='subtitle1' sx={{ fontWeight: 700, color: palette.headerColor, display: 'block', mt: 1, mb: 0.5 }}>
                    {t('achievement-label-instructions')}
                  </Typography>
                  <ReactMarkdown>{instructions}</ReactMarkdown>
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </Modal>
      <CardWithAvatar
        title={title}
        text={cardText}
        src={imageSrc}
        actions={(
          <Button
            size='small'
            className='companion-achievement-info-button CompanionCard-icon'
            startIcon={<InfoOutlinedIcon />}
            onClick={(e) => { e.stopPropagation(); setOpen(true) }}
            sx={{ m: 1 }}
          >
            {t('achievement-show-instructions')}
          </Button>
        )}
      />
    </Box>
  )
}

export const Browser = (props) => {
  const { data, noData, loading } = props
  const { i18n } = useTranslation()
  const { palette } = useThemeMode()
  const themeSx = getThemeSx(palette)

  if (loading && notDefined(data)) {
    return undefined
  }

  if (notDefined(data)) {
    return <NoData />
  } else if (data.length === 0) {
    return <NoData {...noData}/>
  }

  const nodes = data.map((itemProps, key) => {
    const { achievement_id, title, description, instructions, awarded } = itemProps
    const translatedTitle = translateText(title, i18n)
    const translatedDescription = translateText(description, i18n)
    const translatedInstructions = isDefined(instructions)
      ? translateText(instructions, i18n)
      : ''
    const awardedLabel = isDefined(awarded) ? ` (${i18n.t('achievement-awarded')})` : ''
    const imageSrc = itemProps.image_url ?? '/default-achievement.svg'
    return (
      <AchievementCard
        key={achievement_id ?? `${translatedTitle}-${key}`}
        title={translatedTitle}
        description={translatedDescription}
        instructions={translatedInstructions}
        imageSrc={imageSrc}
        awardedLabel={awardedLabel}
      />
    )
  })

  return (
    <Grid container sx={themeSx} className='companion-achievement-browser'>
      {nodes}
    </Grid>
  )
}
